import React, { MutableRefObject, useReducer } from 'react';

import { currentTimeZone, DateTime, getDateTime } from 'infras/datetime';
import {
  APPEND_PAGES_COUNT,
  CalendarMode,
  Errors,
  Schedule,
  SelectedGroup,
  SelectedSlot,
  ShowRange,
  SlotViewType,
  CalendarWeekDay,
  horizontalRulerWidth,
  TimeFormat,
} from '.';
import {
  getCurrentScrollTop,
  getCurrentStartedAt,
  getDefaultPages,
  getNow,
  getRulerStepFromDuration,
  ProviderProps,
  scrollToNow,
  sortSchedules,
  useCalendarProvider,
} from './useCalendarProvider';

interface InitialProps {
  cachePrefix?: string;
  timeZone?: string;
  interval?: number;
  rulerStep?: number;
  rulerBorderStep?: number;
  rulerMinHour?: number;
  rulerMaxHour?: number;
  duration?: number;
  calendarMode?: CalendarMode;
  slotViewType?: SlotViewType;
  schedules?: Schedule[] | null;
  showRange?: ShowRange;
  startWeekday?: CalendarWeekDay;
  errors?: Errors[];
  maxSlotsCount?: number;
  maxSlotGroupsCount?: number;
  rulerTimeZones?: string[];
  isShowSameEventMerge?: boolean;
  isShowEventOverlap?: boolean;
  isShowDeclinedEvent?: boolean;
  hiddenWeekdays?: CalendarWeekDay[];
  timeFormat?: string;
  primaryColor?: string;
  userColor?: string;
  currentDate?: string;
  isDisableMultiTimeZones?: boolean;
  isAutoScrollOnInit?: boolean;
  isDisableHorizontalScroll?: boolean;
}
export interface State {
  cachePrefix: string;
  isReady: boolean;
  isInit: boolean;
  calendarMode: CalendarMode;
  slotViewType: SlotViewType;
  primaryColor: string;
  userColor: string;
  startedAt: DateTime;
  interval: number;
  rulerWidth: number;
  rulerStep: number;
  rulerBorderStep: number;
  rulerMinHour: number;
  rulerMaxHour: number;
  duration: number;
  timeZone: string;
  rulerTimeZones: string[];
  startWeekday: CalendarWeekDay;
  showRange: ShowRange;
  times: DateTime[];
  maxRow: number;
  allDayMaxRow: number;
  schedules: Schedule[] | null;
  selectedSlots: SelectedSlot[];
  selectedGroups: SelectedGroup[];
  currentPage: number;
  pages: DateTime[];
  containerWidth: string | null;
  pageWidth: number;
  containerRef?: MutableRefObject<any>;
  dayTimeGridRef?: MutableRefObject<any>;
  allDayTimeGridRef?: MutableRefObject<any>;
  weekdayHeaderRef?: MutableRefObject<any>;
  now: DateTime;
  errors: Errors[];
  maxSlotsCount?: number;
  maxSlotGroupsCount?: number;
  isShowSameEventMerge: boolean;
  isShowEventOverlap: boolean;
  isShowDeclinedEvent: boolean;
  hiddenWeekdays: CalendarWeekDay[];
  timeFormat: string;
  creatingEvent: Schedule | null;
  isDisableMultiTimeZones?: boolean;
  isAutoScrollOnInit: boolean;
  isDisableHorizontalScroll: boolean;
}

const initialState: (props: InitialProps) => State = (props) => {
  const timeZone = props.timeZone || currentTimeZone();
  const duration = props.duration || 60;
  const rulerStep = props.rulerStep || getRulerStepFromDuration(duration);
  const rulerBorderStep = props.rulerBorderStep || 15;
  const interval = props.interval || rulerStep;
  const showRange = props.showRange || { day: 7 };
  let startWeekday: CalendarWeekDay = props.startWeekday || 0;
  startWeekday = startWeekday > 6 ? 0 : startWeekday;
  const now = getNow(timeZone);
  const startedAt = getCurrentStartedAt({
    current: getDateTime(props.currentDate, timeZone) || now,
    showRange,
    startWeekday,
  });

  const pages = getDefaultPages(startedAt, showRange);
  const currentPage = 1;
  const rulerTimeZones = props.rulerTimeZones || [];
  const extraTimeZoneCount = props.isDisableMultiTimeZones
    ? 0
    : rulerTimeZones.length;
  const rulerWidth = showRange.day
    ? horizontalRulerWidth * (extraTimeZoneCount + 1)
    : 0;
  const isAutoScrollOnInit =
    props.isAutoScrollOnInit !== undefined ? props.isAutoScrollOnInit : true;
  const defaultPrimaryColor = '#1f74f2';
  const rulerMinHour = props.rulerMinHour ?? 0;
  const rulerMaxHour = props.rulerMaxHour ?? 24;

  return {
    cachePrefix: props.cachePrefix || 'root',
    isInit: false,
    isReady: false,
    calendarMode: props.calendarMode || CalendarMode.Event,
    slotViewType: props.slotViewType || SlotViewType.SlotAndGroup,
    startedAt,
    startWeekday,
    interval,
    rulerStep,
    rulerBorderStep,
    rulerMinHour,
    rulerMaxHour,
    maxRow: ((rulerMaxHour - rulerMinHour) * 60) / rulerStep,
    duration,
    allDayMaxRow: 1,
    timeZone,
    showRange,
    times: [],
    schedules: props.schedules || null,
    selectedSlots: [],
    selectedGroups: [],
    pages,
    currentPage,
    containerWidth: null,
    pageWidth: 0,
    now,
    errors: props.errors || [],
    maxSlotsCount: props.maxSlotsCount,
    maxSlotGroupsCount: props.maxSlotGroupsCount,
    rulerWidth,
    rulerTimeZones: props.rulerTimeZones || [],
    isShowSameEventMerge: props.isShowSameEventMerge ?? true,
    isShowEventOverlap: props.isShowEventOverlap ?? true,
    hiddenWeekdays: props.hiddenWeekdays ?? [],
    isShowDeclinedEvent: props.isShowDeclinedEvent ?? true,
    timeFormat: props.timeFormat ?? TimeFormat.Time24,
    creatingEvent: null,
    primaryColor: props.primaryColor || defaultPrimaryColor,
    userColor: props.userColor || defaultPrimaryColor,
    isDisableMultiTimeZones: props.isDisableMultiTimeZones || false,
    isAutoScrollOnInit,
    isDisableHorizontalScroll: props.isDisableHorizontalScroll ?? false,
  };
};

export const setIsInit = (isInit: boolean) =>
  ({
    type: 'SET_IS_INIT',
    isInit,
  } as const);
export const setErrors = (errors: Errors[]) =>
  ({
    type: 'SET_ERRORS',
    errors,
  } as const);
export const setIsReady = (isReady: boolean) =>
  ({
    type: 'SET_IS_READY',
    isReady,
  } as const);
export const setNow = (now: DateTime) =>
  ({
    type: 'SET_NOW',
    now,
  } as const);
export const setPrimaryColor = (primaryColor: string) =>
  ({
    type: 'SET_PRIMARY_COLOR',
    primaryColor,
  } as const);
export const setUserColor = (userColor: string) =>
  ({
    type: 'SET_USER_COLOR',
    userColor,
  } as const);
export const setStartedAt = (startedAt: DateTime) =>
  ({
    type: 'SET_STARTED_AT',
    startedAt,
  } as const);
export const setCalendarMode = (calendarMode: CalendarMode) =>
  ({
    type: 'SET_CALENDAR_MODE',
    calendarMode,
  } as const);
export const setSlotViewType = (slotViewType: SlotViewType) =>
  ({
    type: 'SET_SLOT_VIEW_TYPE',
    slotViewType,
  } as const);
export const setSchedules = (schedules: Schedule[] | null) =>
  ({
    type: 'SET_SCHEDULES',
    schedules,
  } as const);
export const setTimes = (times: DateTime[]) =>
  ({
    type: 'SET_TIMES',
    times,
  } as const);
export const setRulerTimeZones = (rulerTimeZones: string[]) =>
  ({
    type: 'SET_RULER_TIME_ZONES',
    rulerTimeZones,
  } as const);
export const setShowRange = (showRange: ShowRange, startedAt?: DateTime) =>
  ({
    type: 'SET_SHOW_RANGE',
    showRange,
    startedAt,
  } as const);
export const setTimeInterval = (interval: number) =>
  ({
    type: 'SET_TIME_INTERVAL',
    interval,
  } as const);
export const setRulerStep = (rulerStep: number) =>
  ({
    type: 'SET_RULER_STEP',
    rulerStep,
  } as const);
export const setRulerBorderStep = (rulerBorderStep: number) =>
  ({
    type: 'SET_RULER_BORDER_STEP',
    rulerBorderStep,
  } as const);
export const setRulerMinHour = (rulerMinHour: number) =>
  ({
    type: 'SET_RULER_MIN_HOUR',
    rulerMinHour,
  } as const);
export const setRulerMaxHour = (rulerMaxHour: number) =>
  ({
    type: 'SET_RULER_MAX_HOUR',
    rulerMaxHour,
  } as const);
export const setInterval = (interval: number) =>
  ({
    type: 'SET_INTERVAL',
    interval,
  } as const);
export const setDuration = (duration: number) =>
  ({
    type: 'SET_DURATION',
    duration,
  } as const);
export const setTimeZone = (timeZone: string) =>
  ({
    type: 'SET_TIME_ZONE',
    timeZone,
  } as const);
export const setAllDayMaxRow = (allDayMaxRow: number) =>
  ({
    type: 'SET_ALL_DAY_MAX_ROW',
    allDayMaxRow,
  } as const);
export const clearSlots = () =>
  ({
    type: 'CLEAR_SLOTS',
  } as const);
export const setSelectedSlots = (selectedSlots: SelectedSlot[]) =>
  ({
    type: 'SET_SELECTED_SLOTS',
    selectedSlots,
  } as const);
export const setSelectedGroups = (selectedGroups: SelectedGroup[]) =>
  ({
    type: 'SET_SELECTED_GROUPS',
    selectedGroups,
  } as const);

export const setCurrentPage = (currentPage: number) => {
  return {
    type: 'SET_CURRENT_PAGE',
    currentPage,
  } as const;
};

export const scrollToPage = (page: number) =>
  ({
    type: 'SCROLL_TO_PAGE',
    page,
  } as const);

export const setContainerWidth = (containerWidth: string) =>
  ({
    type: 'SET_CONTAINER_WITDH',
    containerWidth,
  } as const);

export const setContainerRef = (containerRef: MutableRefObject<any>) =>
  ({
    type: 'SET_CONTAINER_REF',
    containerRef,
  } as const);

export const setDayTimeGridRef = (dayTimeGridRef: MutableRefObject<any>) =>
  ({
    type: 'SET_DAY_TIME_GRID_REF',
    dayTimeGridRef,
  } as const);

export const setAllDayTimeGridRef = (
  allDayTimeGridRef: MutableRefObject<any>,
) =>
  ({
    type: 'SET_ALL_DAY_TIME_GRID_REF',
    allDayTimeGridRef,
  } as const);
export const setWeekdayHeaderRef = (weekdayHeaderRef: MutableRefObject<any>) =>
  ({
    type: 'SET_WEEKDAY_HEADER_REF',
    weekdayHeaderRef,
  } as const);
export const setCreatingEvent = (creatingEvent: Schedule | null) =>
  ({
    type: 'SET_CREATING_EVENT',
    creatingEvent,
  } as const);

export const setStartWeekday = (startWeekday: CalendarWeekDay) =>
  ({
    type: 'SET_START_WEEKDAY',
    startWeekday,
  } as const);

export const setIsShowSameEventMerge = (isShowSameEventMerge: boolean) =>
  ({
    type: 'SET_IS_SAME_EVENT_MERGIABLE',
    isShowSameEventMerge,
  } as const);

export const setIsShowEventOverlap = (isShowEventOverlap: boolean) =>
  ({
    type: 'SET_IS_EVENT_OVERLAP',
    isShowEventOverlap,
  } as const);

export const setHiddenWeekdays = (hiddenWeekdays: CalendarWeekDay[]) =>
  ({
    type: 'SET_HIDDEN_WEEKDAYS',
    hiddenWeekdays,
  } as const);

export const setIsShowDeclinedEvent = (isShowDeclinedEvent: boolean) =>
  ({
    type: 'SET_IS_SHOW_DECLINED_EVENT',
    isShowDeclinedEvent,
  } as const);

export const setTimeFormat = (timeFormat: string) =>
  ({
    type: 'SET_TIME_FORMAT',
    timeFormat,
  } as const);
export const setIsDisableMultiTimeZones = (isDisableMultiTimeZones: boolean) =>
  ({
    type: 'SET_IS_DISABLE_MULTI_TIME_ZONES',
    isDisableMultiTimeZones,
  } as const);
export const setIsDisableHorizontalScroll = (
  isDisableHorizontalScroll: boolean,
) =>
  ({
    type: 'SET_IS_DISABLE_HORIZONTAL_SCROLL',
    isDisableHorizontalScroll,
  } as const);

export const setRulerWidth = (rulerWidth: number) =>
  ({
    type: 'SET_RULER_WIDTH',
    rulerWidth,
  } as const);

export type Action =
  | ReturnType<typeof setIsInit>
  | ReturnType<typeof setIsReady>
  | ReturnType<typeof setNow>
  | ReturnType<typeof setCalendarMode>
  | ReturnType<typeof setSlotViewType>
  | ReturnType<typeof setTimeInterval>
  | ReturnType<typeof setRulerStep>
  | ReturnType<typeof setRulerWidth>
  | ReturnType<typeof setRulerBorderStep>
  | ReturnType<typeof setInterval>
  | ReturnType<typeof setDuration>
  | ReturnType<typeof setTimeZone>
  | ReturnType<typeof setSchedules>
  | ReturnType<typeof setTimes>
  | ReturnType<typeof setRulerTimeZones>
  | ReturnType<typeof setRulerMinHour>
  | ReturnType<typeof setRulerMaxHour>
  | ReturnType<typeof setShowRange>
  | ReturnType<typeof setAllDayMaxRow>
  | ReturnType<typeof setSelectedSlots>
  | ReturnType<typeof setSelectedGroups>
  | ReturnType<typeof clearSlots>
  | ReturnType<typeof setCurrentPage>
  | ReturnType<typeof setContainerWidth>
  | ReturnType<typeof setContainerRef>
  | ReturnType<typeof setDayTimeGridRef>
  | ReturnType<typeof setAllDayTimeGridRef>
  | ReturnType<typeof setWeekdayHeaderRef>
  | ReturnType<typeof setCreatingEvent>
  | ReturnType<typeof setStartWeekday>
  | ReturnType<typeof setPrimaryColor>
  | ReturnType<typeof setUserColor>
  | ReturnType<typeof setStartedAt>
  | ReturnType<typeof scrollToPage>
  | ReturnType<typeof setErrors>
  | ReturnType<typeof setIsShowEventOverlap>
  | ReturnType<typeof setHiddenWeekdays>
  | ReturnType<typeof setIsShowDeclinedEvent>
  | ReturnType<typeof setTimeFormat>
  | ReturnType<typeof setIsDisableMultiTimeZones>
  | ReturnType<typeof setIsDisableHorizontalScroll>
  | ReturnType<typeof setIsShowSameEventMerge>;

const reducer = (state: State = initialState({}), action: Action) => {
  switch (action.type) {
    case 'SET_IS_INIT':
      return {
        ...state,
        isInit: action.isInit,
      };
    case 'SET_ERRORS':
      return {
        ...state,
        errors: action.errors,
      };
    case 'SET_NOW':
      return {
        ...state,
        now: action.now,
      };
    case 'SET_PRIMARY_COLOR':
      return {
        ...state,
        primaryColor: action.primaryColor,
      };
    case 'SET_USER_COLOR':
      return {
        ...state,
        userColor: action.userColor,
      };
    case 'SET_STARTED_AT':
      return {
        ...state,
        startedAt: action.startedAt,
      };
    case 'SET_IS_READY': {
      if (action.isReady === true) {
        // 初回表示スクロール
        if (state.showRange.day) {
          const left = state.pageWidth * state.currentPage;
          if (state.dayTimeGridRef?.current) {
            state.dayTimeGridRef.current.scrollLeft = left;
          }
          if (state.allDayTimeGridRef?.current) {
            state.allDayTimeGridRef.current.scrollLeft = left;
          }
          if (state.weekdayHeaderRef?.current) {
            state.weekdayHeaderRef.current.scrollLeft = left;
          }
          if (state.isAutoScrollOnInit) {
            scrollToNow(state.timeZone);
          }
        } else if (state.showRange.month) {
          const top = getCurrentScrollTop(state.currentPage);
          window.scrollTo(0, top);
        }
      }
      return {
        ...state,
        isReady: action.isReady,
      };
    }
    case 'SET_CALENDAR_MODE':
      return {
        ...state,
        calendarMode: action.calendarMode,
      };
    case 'SET_SLOT_VIEW_TYPE':
      return {
        ...state,
        slotViewType: action.slotViewType,
      };
    case 'SET_SCHEDULES':
      const schedules = action.schedules
        ? sortSchedules(action.schedules, state.timeZone)
        : null;
      return {
        ...state,
        schedules,
      };
    case 'SCROLL_TO_PAGE': {
      if (state.showRange.day && state.dayTimeGridRef?.current) {
        state.dayTimeGridRef!.current.scrollLeft =
          state.pageWidth * action.page;
      } else if (state.showRange.month) {
        window.scrollTo(0, getCurrentScrollTop(action.page));
      }
      return {
        ...state,
      };
    }
    case 'SET_CURRENT_PAGE': {
      if (state.currentPage === action.currentPage) {
        return state;
      }
      const startedAt = state.pages[action.currentPage];

      const currentPage = action.currentPage;
      const pages = [...state.pages];
      if (currentPage === 0) {
        // 過去ページを作成
        for (let i = 1; i <= APPEND_PAGES_COUNT; i++) {
          let d;
          if (state.showRange.day) {
            d = { day: state.showRange.day * i };
          } else if (state.showRange.month) {
            d = { month: state.showRange.month * i };
          }
          if (!d) break;
          pages.unshift(startedAt.minus(d));
        }
      } else if (currentPage === state.pages.length - 1) {
        // 未来ページを作成
        for (let i = 1; i <= APPEND_PAGES_COUNT; i++) {
          let d;
          if (state.showRange.day) {
            d = { day: state.showRange.day * i };
          } else if (state.showRange.month) {
            d = { month: state.showRange.month * i };
          }
          if (!d) break;
          pages.push(startedAt.plus(d));
        }
      }

      return {
        ...state,
        currentPage,
        pages,
        startedAt,
      };
    }
    case 'SET_TIME_INTERVAL': {
      if (state.interval === action.interval) {
        return state;
      }
      return {
        ...state,
        interval: action.interval,
        selectedSlots: [],
        selectedGroups: [],
      };
    }
    case 'SET_RULER_BORDER_STEP': {
      return {
        ...state,
        rulerBorderStep: action.rulerBorderStep,
      };
    }
    case 'SET_RULER_MIN_HOUR': {
      const maxRow =
        ((state.rulerMaxHour - action.rulerMinHour) * 60) / state.rulerStep;
      return {
        ...state,
        rulerMinHour: action.rulerMinHour,
        maxRow,
      };
    }
    case 'SET_RULER_MAX_HOUR': {
      const maxRow =
        ((action.rulerMaxHour - state.rulerMinHour) * 60) / state.rulerStep;
      return {
        ...state,
        rulerMaxHour: action.rulerMaxHour,
        maxRow,
      };
    }
    case 'SET_RULER_STEP': {
      if (state.rulerStep === action.rulerStep) {
        return state;
      }
      const maxRow =
        ((state.rulerMaxHour - state.rulerMinHour) * 60) / action.rulerStep;
      return {
        ...state,
        rulerStep: action.rulerStep,
        maxRow,
      };
    }
    case 'SET_DURATION': {
      const rulerStep = getRulerStepFromDuration(action.duration);
      const maxRow =
        ((state.rulerMaxHour - state.rulerMinHour) * 60) / rulerStep;
      return {
        ...state,
        duration: action.duration,
        rulerStep,
        interval: rulerStep,
        maxRow,
      };
    }
    case 'SET_TIME_ZONE': {
      return {
        ...state,
        timeZone: action.timeZone,
        now: getNow(action.timeZone),
      };
    }
    case 'SET_RULER_TIME_ZONES': {
      const extraTimeZoneCount = state.isDisableMultiTimeZones
        ? 0
        : action.rulerTimeZones.length;
      const rulerWidth = state.showRange.day
        ? horizontalRulerWidth * (extraTimeZoneCount + 1)
        : 0;
      return {
        ...state,
        rulerTimeZones: action.rulerTimeZones,
        rulerWidth,
      };
    }
    case 'SET_TIMES':
      return {
        ...state,
        times: action.times,
      };
    case 'SET_SHOW_RANGE': {
      const startedAt =
        action.startedAt ||
        getCurrentStartedAt({
          current: getNow(state.timeZone),
          showRange: action.showRange,
          startWeekday: state.startWeekday,
        });
      const pages = getDefaultPages(startedAt, action.showRange);
      const currentPage = 1;

      const extraTimeZoneCount = state.isDisableMultiTimeZones
        ? 0
        : state.rulerTimeZones.length;
      const rulerWidth = action.showRange.day
        ? horizontalRulerWidth * (extraTimeZoneCount + 1)
        : 0;

      return {
        ...state,
        showRange: action.showRange,
        startedAt,
        pages,
        currentPage,
        rulerWidth,
      };
    }
    case 'SET_ALL_DAY_MAX_ROW':
      return {
        ...state,
        allDayMaxRow: action.allDayMaxRow,
      };
    case 'CLEAR_SLOTS':
      return {
        ...state,
        selectedSlots: [],
        selectedGroups: [],
      };
    case 'SET_SELECTED_SLOTS':
      return {
        ...state,
        selectedSlots: action.selectedSlots,
      };
    case 'SET_SELECTED_GROUPS':
      return {
        ...state,
        selectedGroups: action.selectedGroups,
      };
    case 'SET_CONTAINER_WITDH':
      const pageWidth = parseInt(action.containerWidth!, 10) - state.rulerWidth;
      return {
        ...state,
        containerWidth: action.containerWidth,
        pageWidth,
      };
    case 'SET_CONTAINER_REF':
      return {
        ...state,
        containerRef: action.containerRef,
      };
    case 'SET_DAY_TIME_GRID_REF':
      return {
        ...state,
        dayTimeGridRef: action.dayTimeGridRef,
      };
    case 'SET_ALL_DAY_TIME_GRID_REF':
      return {
        ...state,
        allDayTimeGridRef: action.allDayTimeGridRef,
      };
    case 'SET_WEEKDAY_HEADER_REF':
      return {
        ...state,
        weekdayHeaderRef: action.weekdayHeaderRef,
      };
    case 'SET_CREATING_EVENT':
      return {
        ...state,
        creatingEvent: action.creatingEvent,
      };
    case 'SET_START_WEEKDAY': {
      const startedAt = getCurrentStartedAt({
        current: getNow(state.timeZone),
        showRange: state.showRange,
        startWeekday: action.startWeekday,
      });
      const pages = getDefaultPages(startedAt, state.showRange);
      const currentPage = 1;
      return {
        ...state,
        startWeekday: action.startWeekday,
        pages,
        currentPage,
      };
    }
    case 'SET_IS_EVENT_OVERLAP':
      return {
        ...state,
        isShowEventOverlap: action.isShowEventOverlap,
      };
    case 'SET_HIDDEN_WEEKDAYS':
      return {
        ...state,
        hiddenWeekdays: action.hiddenWeekdays,
      };
    case 'SET_IS_SHOW_DECLINED_EVENT':
      return {
        ...state,
        isShowDeclinedEvent: action.isShowDeclinedEvent,
      };
    case 'SET_TIME_FORMAT':
      return {
        ...state,
        timeFormat: action.timeFormat,
      };
    case 'SET_IS_SAME_EVENT_MERGIABLE':
      return {
        ...state,
        isShowSameEventMerge: action.isShowSameEventMerge,
      };
    case 'SET_IS_DISABLE_MULTI_TIME_ZONES':
      return {
        ...state,
        isDisableMultiTimeZones: action.isDisableMultiTimeZones,
      };
    case 'SET_RULER_WIDTH':
      return {
        ...state,
        rulerWidth: action.rulerWidth,
      };
    case 'SET_IS_DISABLE_HORIZONTAL_SCROLL':
      return {
        ...state,
        isDisableHorizontalScroll: action.isDisableHorizontalScroll,
      };
    default:
      return state;
  }
};

const CalendarContext = React.createContext<{
  state: State;
  dispatch: (action: Action) => void;
}>({
  state: initialState({}),
  dispatch: () => {},
});

const CalendarProvider = (props: ProviderProps) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialState({
      cachePrefix: props.cachePrefix,
      currentDate: props.currentDate,
      timeZone: props.timeZone,
      interval: props.interval,
      rulerStep: props.rulerStep,
      rulerBorderStep: props.rulerBorderStep,
      rulerMinHour: props.rulerMinHour,
      rulerMaxHour: props.rulerMaxHour,
      duration: props.duration,
      calendarMode: props.calendarMode,
      slotViewType: props.slotViewType,
      startWeekday: props.startWeekday,
      showRange: props.showRange,
      schedules: props.schedules || null,
      maxSlotsCount: props.maxSlotsCount,
      maxSlotGroupsCount: props.maxSlotGroupsCount,
      rulerTimeZones: props.rulerTimeZones,
      isShowSameEventMerge: props.isShowSameEventMerge,
      isShowEventOverlap: props.isShowEventOverlap,
      timeFormat: props.timeFormat,
      isShowDeclinedEvent: props.isShowDeclinedEvent,
      hiddenWeekdays: props.hiddenWeekdays,
      primaryColor: props.primaryColor,
      userColor: props.userColor,
      isDisableMultiTimeZones: props.isDisableMultiTimeZones,
      isAutoScrollOnInit: props.isAutoScrollOnInit,
      isDisableHorizontalScroll: props.isDisableHorizontalScroll,
    }),
  );

  useCalendarProvider(props, { state, dispatch });

  return (
    <CalendarContext.Provider value={{ state, dispatch }}>
      {props.children}
    </CalendarContext.Provider>
  );
};

export { CalendarContext, CalendarProvider };
